import { WORDS_PER_SECOND } from '@reelmachine/core';

/**
 * Reading pace of the teleprompter roll. Default 150 words per minute (2.5 words/s, the rate our script timing
 * assumes): the top of the 140 to 160 wpm band recommended for short-form video, and close to the ~5 syllables/s
 * of German read speech (about 1.9 syllables per word). Measured on our first reel: 163 wpm including pauses.
 */
export const DEFAULT_WPM = Math.round(WORDS_PER_SECOND * 60);
export const MIN_WPM = 110;
export const MAX_WPM = 200;

/** Seconds per stage direction: [Pause] is a real pause, other directions ([Blick halten]) a short beat. */
const PAUSE_S = 0.8;
const DIRECTION_S = 0.4;
/** Breath between two lines. */
const GAP_S = 0.25;

export function lineSeconds(text: string, wpm: number): number {
  const directions = text.match(/\[[^\]]*\]/g) ?? [];
  const words = text
    .replace(/\[[^\]]*\]/g, ' ')
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
  const pauses = directions.reduce((s, d) => s + (/pause/i.test(d) ? PAUSE_S : DIRECTION_S), 0);
  return (words * 60) / Math.max(1, wpm) + pauses + GAP_S;
}

/** Start time (s) of every line, plus the total as the last entry. */
export function lineStarts(lines: string[], wpm: number): number[] {
  const out = [0];
  for (const l of lines) out.push(out[out.length - 1]! + lineSeconds(l, wpm));
  return out;
}

/**
 * Roll offset in px at time t: each line takes as long as its words need, whatever its height on screen, so a
 * short line does not rush by and a wrapped long line does not crawl.
 */
export function rollOffset(t: number, starts: number[], tops: number[], end: number): { y: number; line: number } {
  const n = tops.length;
  if (!n) return { y: 0, line: 0 };
  let i = 0;
  while (i < n - 1 && t >= starts[i + 1]!) i++;
  const from = tops[i]!;
  const to = i + 1 < n ? tops[i + 1]! : end;
  const span = Math.max(0.001, starts[i + 1]! - starts[i]!);
  const f = Math.max(0, Math.min(1, (t - starts[i]!) / span));
  return { y: from + f * (to - from), line: i };
}
